import { useSuspenseQuery } from "@apollo/client/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2 } from "lucide-react";
import { Suspense } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useShowValidationErrors } from "~/common/hooks";
import { DetailLoading } from "~/components/loading";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Switch,
} from "~/components/ui";
import { GetTenantDocument, type GetTenantQuery, type GetTenantQueryVariables } from "~/features/admin/tenants/gqls";
import { useTenantCreate, useTenantEdit } from "~/features/admin/tenants/hooks";
import {
  TENANT_SHORTCODE_PREFIX,
  type TenantEditFormData,
  type TenantFormData,
  zTenantEditFormData,
  zTenantEditFormDataKeys,
  zTenantFormData,
  zTenantFormDataKeys,
} from "~/features/admin/tenants/types";

interface TenantFormDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** When set, the dialog is in edit mode and prefills from this tenant id. */
  tenantId?: string;
}

const FORM_ID = "tenant-form";

export function TenantFormDialog({ open, onOpenChange, tenantId }: TenantFormDialogProps) {
  const { t } = useTranslation();
  const isEdit = Boolean(tenantId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="capitalize">
            {isEdit
              ? `${t("ui.edit")} ${t("model.tenant.modelName")}`
              : `${t("ui.add")} ${t("model.tenant.modelName")}`}
          </DialogTitle>
        </DialogHeader>
        <Suspense fallback={<DetailLoading />}>
          {isEdit && tenantId ? (
            <EditBody tenantId={tenantId} onClose={() => onOpenChange(false)} />
          ) : (
            <CreateBody onClose={() => onOpenChange(false)} />
          )}
        </Suspense>
      </DialogContent>
    </Dialog>
  );
}

function CreateBody({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const form = useForm<TenantFormData>({
    resolver: zodResolver(zTenantFormData),
    defaultValues: {
      name: "",
      shortCode: "",
      allowAutoApproveChangeRequests: false,
    },
  });
  const showValidationErrors = useShowValidationErrors(form.setError, zTenantFormDataKeys);
  const { submit, processing } = useTenantCreate({
    onSuccess: () => {
      form.reset();
      onClose();
    },
    onError: (e) => showValidationErrors(e.fields),
  });

  return (
    <Form {...form}>
      <form id={FORM_ID} onSubmit={form.handleSubmit(submit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.name")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="shortCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.tenant.shortCode")}</FormLabel>
              <FormControl>
                <div className="flex items-center rounded-xs border border-border-strong bg-white focus-within:ring-2 focus-within:ring-primary-300 focus-within:ring-offset-2 focus-within:ring-offset-white">
                  <span className="select-none border-r border-border-default px-2.5 py-2 font-mono text-14-regular text-fg-3">
                    {TENANT_SHORTCODE_PREFIX}
                  </span>
                  <Input {...field} className="border-0 font-mono focus-visible:ring-0 focus-visible:ring-offset-0" />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <AutoApproveField
          // react-hook-form v7 isn't React Compiler-compatible; form.watch() is
          // flagged by the react-hooks/incompatible-library Compiler rule.
          // eslint-disable-next-line react-hooks/incompatible-library
          checked={form.watch("allowAutoApproveChangeRequests")}
          onCheckedChange={(v) => form.setValue("allowAutoApproveChangeRequests", v)}
        />
      </form>
      <Footer processing={processing} />
    </Form>
  );
}

function EditBody({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const { data } = useSuspenseQuery<GetTenantQuery, GetTenantQueryVariables>(GetTenantDocument, {
    variables: { id: tenantId },
  });
  const tenant = data.tenantsByPk;

  const form = useForm<TenantEditFormData>({
    resolver: zodResolver(zTenantEditFormData),
    defaultValues: {
      id: tenantId,
      name: tenant?.name ?? "",
      allowAutoApproveChangeRequests: tenant?.allowAutoApproveChangeRequests ?? false,
    },
  });
  const showValidationErrors = useShowValidationErrors(form.setError, zTenantEditFormDataKeys);
  const { submit, processing } = useTenantEdit({
    onSuccess: () => {
      form.reset();
      onClose();
    },
    onError: (e) => showValidationErrors(e.fields),
  });

  if (!tenant) return <div className="py-6 text-center text-14-regular text-fg-3">—</div>;

  return (
    <Form {...form}>
      <form id={FORM_ID} onSubmit={form.handleSubmit(submit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("model.common.name")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormItem>
          <FormLabel>{t("model.tenant.shortCode")}</FormLabel>
          <FormControl>
            <Input value={tenant.shortCode} disabled className="font-mono" />
          </FormControl>
        </FormItem>
        <AutoApproveField
          // react-hook-form v7 isn't React Compiler-compatible; form.watch() is
          // flagged by the react-hooks/incompatible-library Compiler rule.
          // eslint-disable-next-line react-hooks/incompatible-library
          checked={form.watch("allowAutoApproveChangeRequests")}
          onCheckedChange={(v) => form.setValue("allowAutoApproveChangeRequests", v)}
        />
      </form>
      <Footer processing={processing} />
    </Form>
  );
}

function AutoApproveField({
  checked,
  onCheckedChange,
}: {
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
}) {
  const { t } = useTranslation();
  return (
    <FormItem className="flex flex-row items-center justify-between gap-4 rounded-md border border-border-default p-3">
      <FormLabel className="capitalize">{t("model.tenant.allowAutoApproveChangeRequests")}</FormLabel>
      <FormControl>
        <Switch checked={checked} onCheckedChange={onCheckedChange} />
      </FormControl>
    </FormItem>
  );
}

function Footer({ processing }: { processing: boolean }) {
  const { t } = useTranslation();
  return (
    <DialogFooter>
      <DialogClose asChild>
        <Button type="button" variant="outline" disabled={processing}>
          {t("ui.cancel")}
        </Button>
      </DialogClose>
      <Button type="submit" form={FORM_ID} disabled={processing}>
        {processing && <Loader2 className="animate-spin" />}
        <span>{t("ui.save")}</span>
      </Button>
    </DialogFooter>
  );
}
